/**
 * SitePick front — クーポン適用 / 解除（docs/15 §15.20）
 *
 * `POST /coupon/apply` に `{ product_id, code }` を送り、サーバー側で検証・価格計算した
 * 結果を表示する。**価格・割引額はクライアントから送らない**——購入時の請求額は
 * 予約作成時にサーバーが再評価して確定する（ここで見せるのは確認用のプレビュー）。
 *
 * Checkout の POST 契約は変わらない（`product_id` のみ）。クーポンの意思は
 * サーバーが発行する署名付き Cookie が運ぶ。
 *
 * パートナー制度（docs/21 §3.3・§A.1）: 制度 ON のときだけテーマが `.js-sp-partner-note` を出す（OFF は要素が無く、
 * 以下のパートナー分岐はどれも通らない = 従来どおり）。紹介コードも同じ `POST /coupon/apply` で、応答の形は同じ
 * （label =「紹介コード」+ `kind: "referral"`）。サーバーが描いた適用中の状態（data-sp-partner-*）は、価格ブロックを既存のクーポン適用と
 * 同じ「ラベル + 割引後の価格」で描き直すだけ（金額はサーバーの値。JS で計算しない・通常価格を並べない）。
 */

import { announce } from "./live-region.js";

function restBase() {
  const cfg = window.SITEPICK || {};
  const base = typeof cfg.restUrl === "string" && cfg.restUrl ? cfg.restUrl : "/wp-json/sitepick/v1/";
  return base.endsWith("/") ? base : `${base}/`;
}

const yen = (n) => `¥${Number(n).toLocaleString("ja-JP")}`;

/** パートナー制度のエラー文言（REST の error コード → 表示）。 */
const PARTNER_ERRORS = {
  coupon_sale_excluded: "セール対象の商品には紹介コードをご利用いただけません。",
};

/**
 * パートナーの注記（制度 ON のときだけテーマが出す）。OFF は null。
 *
 * @returns {HTMLElement|null}
 */
function partnerNote() {
  const el = document.querySelector(".js-sp-partner-note");
  return el instanceof HTMLElement ? el : null;
}

const escapeHtml = (s) =>
  String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);

/** サーバー側 `sitepick_price_html()` と同じマークアップ。 */
const priceHtml = (n) =>
  `<data class="p-price" value="${Number(n)}">${Number(n).toLocaleString("ja-JP")}<span class="p-price__unit">円</span></data>`;

/** 価格表示を更新する（商品詳細と追従購入バーで同じ値を出す）。 */
function paintPrice(root, data) {
  const targets = document.querySelectorAll("[data-sp-price-block]");
  for (const el of targets) {
    if (!data || !data.applied) {
      // 解除時は自動 Promotion だけの価格（サーバーが描いた初期状態）へ戻す。
      if (el.dataset.spInitial) el.innerHTML = el.dataset.spInitial;
      continue;
    }
    if (!el.dataset.spInitial) el.dataset.spInitial = el.innerHTML;
    // サーバー描画（`sitepick_price_block_html()`）とまったく同じ構造にする。
    // 片方だけ違うマークアップだと、同じ価格でも見た目や解析結果がずれる。
    // 通常価格は並べない（二重価格表示をしない。2026-10-01 人間判断）。ラベル → 割引後の価格だけ。
    const label = typeof data.label === "string" && data.label ? data.label : "クーポン適用";
    el.innerHTML =
      "<span class=\"p-price-block\">" +
      `<span class="p-price-block__label">${escapeHtml(label)}</span>` +
      `<span class="p-price-block__sale">${priceHtml(data.effective_price)}</span>` +
      "</span>";
  }
  root.dataset.spCouponApplied = data && data.applied ? "1" : "";
}

function setStatus(root, text, isError) {
  const box = root.querySelector(".js-sp-coupon-status");
  if (!box) return;
  box.textContent = text || "";
  box.classList.toggle("is-error", Boolean(isError));
}

async function send(path, body) {
  const cfg = window.SITEPICK || {};
  const res = await fetch(`${restBase()}${path}`, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "content-type": "application/json",
      ...(cfg.nonce ? { "X-WP-Nonce": cfg.nonce } : {}),
    },
    body: JSON.stringify(body || {}),
  });
  let data = null;
  try { data = await res.json(); } catch { /* 空ボディ */ }
  if (!res.ok) {
    const err = new Error((data && data.message) || "エラーが発生しました。");
    err.data = data;
    throw err;
  }
  return data;
}

export function initCoupon() {
  const root = document.querySelector(".js-sp-coupon");
  if (!root) return;

  const productId = Number(root.dataset.spProductId || 0);
  const input = root.querySelector(".js-sp-coupon-input");
  const applyBtn = root.querySelector(".js-sp-coupon-apply");
  const clearBtn = root.querySelector(".js-sp-coupon-clear");
  if (!productId || !input || !applyBtn) return;

  initPartnerNote(root, clearBtn);

  applyBtn.addEventListener("click", async () => {
    const code = input.value.trim();
    if (!code) {
      setStatus(root, "クーポンコードを入力してください。", true);
      return;
    }
    applyBtn.disabled = true;
    setStatus(root, "確認しています…", false);
    try {
      const data = await send("coupon/apply", { product_id: productId, code });
      paintPrice(root, data);
      const note = partnerNote();
      // 紹介コードかどうかは応答の kind で判定する（label の文言には依存しない。実クーポンの応答には kind が無い）。
      const isPartner = Boolean(note) && data && data.kind === "referral";
      if (note) {
        // 入力したコードがサーバー側で最優先（sp_cc > sp_self > sp_ref）になるので、サーバーが描いた適用中の注記と
        // SELF の解除ボタンは古くなる。隠す（解除すると再読込でサーバーの表示に戻る）。
        for (const el of note.querySelectorAll(".c-coupon-partner__status, .js-sp-partner-self-clear")) el.hidden = true;
        // checkout.js が 409 の文言を選ぶための印（手入力の紹介コード = referral。実クーポンなら外す）。
        if (isPartner) {
          note.dataset.spPartnerKind = "referral";
          note.dataset.spPartnerSource = "coupon";
        } else {
          delete note.dataset.spPartnerKind;
          delete note.dataset.spPartnerSource;
        }
      }
      setStatus(root, isPartner
        ? `紹介コードを適用しました（${yen(data.discount_amount)} 引き）。`
        : `クーポンを適用しました（${yen(data.discount_amount)} 引き）。`, false);
      announce(isPartner ? "紹介コードを適用しました。" : "クーポンを適用しました。");
      if (clearBtn) clearBtn.hidden = false;
    } catch (err) {
      paintPrice(root, null);
      const code = err.data && typeof err.data.error === "string" ? err.data.error : "";
      setStatus(root, PARTNER_ERRORS[code] || err.message, true);
      if (clearBtn) clearBtn.hidden = true;
    } finally {
      applyBtn.disabled = false;
    }
  });

  if (clearBtn) {
    clearBtn.addEventListener("click", async () => {
      clearBtn.disabled = true;
      try {
        await send("coupon/clear", {});
        if (partnerNote()) {
          // 制度 ON: 紹介リンク（sp_ref）や本人の購入（sp_self）が残っていれば価格が変わるため、サーバーの表示に描き直す。
          window.location.reload();
          return;
        }
        paintPrice(root, null);
        setStatus(root, "クーポンを解除しました。", false);
        announce("クーポンを解除しました。");
        clearBtn.hidden = true;
        input.value = "";
      } catch (err) {
        setStatus(root, err.message, true);
      } finally {
        clearBtn.disabled = false;
      }
    });
  }
}

/**
 * パートナーの注記（制度 ON のときだけ存在）。
 *
 * - サーバーが「適用中」と描いたときは、価格ブロックをその価格（data-sp-partner-price）で描く。
 * - 手入力の紹介コード（source = coupon）は既存の「クーポンを解除する」で解除できるように見せる。
 * - 本人の購入（SELF）の「パートナー価格を解除する」は `POST /coupon/clear`（partner=self）→ 再読込。
 *
 * @param {HTMLElement} root `.js-sp-coupon`
 * @param {HTMLButtonElement|null} clearBtn
 * @returns {void}
 */
function initPartnerNote(root, clearBtn) {
  const note = partnerNote();
  if (!note) return;

  const price = Number(note.dataset.spPartnerPrice || "");
  if (note.dataset.spPartnerStatus === "applied" && Number.isFinite(price) && price > 0) {
    paintPrice(root, { applied: true, label: note.dataset.spPartnerLabel || "", effective_price: price });
    if (note.dataset.spPartnerSource === "coupon" && clearBtn) clearBtn.hidden = false;
  }

  const selfClear = note.querySelector(".js-sp-partner-self-clear");
  if (!(selfClear instanceof HTMLButtonElement)) return;
  selfClear.addEventListener("click", async () => {
    selfClear.disabled = true;
    try {
      await send("coupon/clear", { partner: "self" });
      announce("パートナー価格を解除しました。");
      window.location.reload();
    } catch (err) {
      setStatus(root, err.message, true);
      selfClear.disabled = false;
    }
  });
}
